/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:max-inline-declarations

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { PositionService } from './position.service';
import { SplitPaneComponent } from './split-pane.component';

@Component({
  selector: 'gh-vertical-split-pane',
  styles: [`
      .v-outer {
          height: 100%;
          width: 100%;
          display: flex;
      }
      .left-full .left-component {
        width: calc(100% - 4px)
      }
      .left-full .right-component {
        width: 0;
      }
      .left-component {
          width: calc(50% - 4px);
      }

      .right-component {
          width: calc(50% - 4px);
      }
  `],
  template: `
    <div #outer class="v-outer" [ngClass]="{'left-full': expanded}">
      <div
        #primaryComponent
        [hidden]="primaryToggledOff"
        class="left-component">
        <ng-content select=".split-pane-content-primary"></ng-content>
      </div>
      <gh-vertical-split-separator
        [ngClass]="{'can-collapse': canCollapsed }"
        #separator
        [hidden]="primaryToggledOff || secondaryToggledOff"
        [thickness]="separatorThickness"
        [canCollapsed]="canCollapsed"
        [showExpand]="showExpand"
        (collapseClick)="onCollapseCLick()"
        (expandClick)="onExpandCLick()"
        (notifyWillChangeSize)="notifyWillChangeSize($event)">
      </gh-vertical-split-separator>
      <div
        #secondaryComponent
        [hidden]="secondaryToggledOff"
        class="right-component">
        <ng-content select=".split-pane-content-secondary"></ng-content>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerticalSplitPaneComponent extends SplitPaneComponent {

  @ViewChild('outer', { static: false }) outerContainer: ElementRef;
  // tslint:disable-next-line:no-input-prefix
  @Input() canCollapsed: boolean;
  get showExpand(): boolean {
    // tslint:disable-next-line:no-magic-numbers
    return this.getPrimarySize() >= this.getSecondarySize() + 15;
  }
  constructor(
    element: ElementRef,
    private changeDetector: ChangeDetectorRef,
  ) {
    super(element);
  }

  getTotalSize(): number {
    if (!this.outerContainer || !this.outerContainer?.nativeElement || !this.outerContainer?.nativeElement?.offsetWidth) {
      return 0;
    }
    return this.outerContainer?.nativeElement?.offsetWidth;
  }

  getPrimarySize(): number {
    if (!this.primaryComponent || !this.primaryComponent?.nativeElement || !this.primaryComponent?.nativeElement?.offsetWidth) {
      return 0;
    }
    return this.primaryComponent?.nativeElement?.offsetWidth ;
  }

  getSecondarySize(): number {
    if (!this.secondaryComponent || !this.secondaryComponent?.nativeElement || !this.secondaryComponent?.nativeElement?.offsetWidth) {
      return 0;
    }
    return this.secondaryComponent?.nativeElement?.offsetWidth;
  }

  dividerPosition(size: number): void {
    // tslint:disable-next-line:no-magic-numbers
    const sizePct = (size / this.getTotalSize()) * 100;
    // tslint:disable-next-line:no-magic-numbers
    this.expanded = sizePct < 99;
    this.primaryComponent.nativeElement.style.width =  sizePct + '%';
    this.secondaryComponent.nativeElement.style.width =
      // tslint:disable-next-line:no-magic-numbers
      `calc(${100 - sizePct}% - ${this.primaryToggledOff || this.secondaryToggledOff ? 0 : this.separatorThickness}px)`;
  }
  onCollapseCLick(): void {
    const pixelsInStep = 50;
    const stepTime = 20; // ms;
    const totalSize = this.getTotalSize();
    let size = this.getPrimarySize();
    const timer = setInterval(() => {
      if (size < totalSize) {
        if (totalSize - size > pixelsInStep) {
          size += pixelsInStep;
          this.dividerPosition(size);
        } else {
          size = totalSize;
          this.dividerPosition(totalSize);
          clearInterval(timer);
          this.notifySizeDidChange.emit({ 'primary': 1});
          this.changeDetector.detectChanges();
        }
      } else {
        clearInterval(timer);
        this.notifySizeDidChange.emit({ 'primary': 1});
        this.changeDetector.detectChanges();
      }
    },                        stepTime);
  }
  onExpandCLick(): void {
    const pixelsInStep = 50;
    const stepTime = 20; // ms;
    const totalSize = this.getTotalSize();
    // tslint:disable-next-line:no-magic-numbers
    const halfSize = totalSize / 2;
    let size = this.getPrimarySize();
    const targetSize = halfSize;
    const timer = setInterval(() => {
      if (size > targetSize) {
        if (size - pixelsInStep > targetSize) {
          size -= pixelsInStep;
          this.dividerPosition(size);
        } else {
          size = targetSize;
          this.applySizeChange(targetSize);
          clearInterval(timer);
          this.changeDetector.detectChanges();
        }
      } else {
        this.applySizeChange(targetSize);
        clearInterval(timer);
        this.changeDetector.detectChanges();
      }
    },                        stepTime);
  }

  onMouseMove(event: MouseEvent): void {
    const coords = PositionService.offset(this.primaryComponent);
    this.applySizeChange(event.pageX - coords.left);
    event.preventDefault();
  }
}
